import { useSerialDevice } from "@/contexts/serial-device"
import {
  DEVICE_CONNECTED,
  DEVICE_DISCONNECTED,
  DEVICE_UNSUPPORTED,
  JDS_LABS_DEVICE_IDS,
} from "@/constants"
import cx from "classnames"
import { useState } from "react"
import ConnectionDetailsModal from "@/components/modals/ConnectionDetailsModal"

type StatusProps = {
  theme: String
  children: React.ReactNode
  onClick?: () => void
}

const Status = ({ theme, children, onClick }: StatusProps) => {
  let Component: React.ElementType = onClick ? "button" : "div"

  return (
    <div className="group relative pb-2.5 pt-3">
      <Component
        className={cx("type-body-sm flex h-7 items-center gap-x-2 transition-all", {
          "text-bamboo-light": theme === "green",
          "hover:text-shadow-lg hover:shadow-bamboo": !!onClick && theme === "green",
          "text-red": theme === "red",
          "hover:text-shadow-lg hover:shadow-red": !!onClick && theme === "red",
          "text-yellow": theme === "yellow",
          "hover:text-shadow-lg hover:shadow-yellow": !!onClick && theme === "red",
        })}
        onClick={onClick}
      >
        <div
          className={cx("h-4 w-4 rounded-full bg-gradient-to-b", {
            "to-dark-bamboo from-bamboo": theme === "green",
            "from-red to-dark-red": theme === "red",
            "to-dark-yellow from-yellow": theme === "yellow",
          })}
        ></div>
        <span>{children}</span>
      </Component>
      <div
        className={cx(
          "absolute -bottom-px -left-10 -right-10 h-px bg-gradient-to-r from-transparent to-transparent transition-all",
          {
            "group-hover:-left-16 group-hover:-right-16": !!onClick,
            "via-bamboo": theme === "green",
            "via-red": theme === "red",
            "via-yellow": theme === "yellow",
          }
        )}
      ></div>
    </div>
  )
}

const DeviceStatus = () => {
  const serialDevice = useSerialDevice()
  const [showDetails, setShowDetails] = useState(false)

  const isConnected = serialDevice.status === DEVICE_CONNECTED
  const isUnsupported = serialDevice.status === DEVICE_UNSUPPORTED
  const isDisconnected = serialDevice.status === DEVICE_DISCONNECTED

  const getDeviceName = () => {
    try {
      const portInfo = serialDevice.portRef?.current?.getInfo()
      return portInfo ? JDS_LABS_DEVICE_IDS[portInfo.usbProductId] : ""
    } catch (error) {
      return ""
    }
  }

  const getPortInfo = () => {
    try {
      return serialDevice.portRef?.current?.getInfo() || {}
    } catch (error) {
      return {}
    }
  }

  return (
    <>
      {isConnected && (
        <Status theme="green" onClick={() => setShowDetails(true)}>
          Connected
        </Status>
      )}
      {isDisconnected && <Status theme="red">Disconnected</Status>}
      {isUnsupported && <Status theme="yellow">Unsupported Device</Status>}
      <ConnectionDetailsModal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        deviceName={getDeviceName()}
        portInfo={getPortInfo()}
      />
    </>
  )
}

export default DeviceStatus
